import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { formatNumber } from "../../utils/helpers";

// Import the icons
import SettingsIcon from "../../assets/icons/settings.svg";
import MuteIcon from "../../assets/icons/mute.svg";
import UnmuteIcon from "../../assets/icons/unmute.svg";
import PauseIcon from "../../assets/icons/pause.png";
import PlayIcon from "../../assets/icons/play.png";
import { getLevel } from "../../utils/constants";

const ProfileContainer = styled.div`
  display: flex;
  color: white;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  gap: 0.4rem;
  padding: 1rem 1rem;
  font-weight: 700;
  z-index: 20;
  position: relative;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
`;

const BackgroundOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.1),
    rgba(0, 0, 0, 0.7)
  );
  z-index: -1;
`;

const AvatarWrapper = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 2.6rem;
  background-color: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 255, 255, 0.3);
  cursor: pointer;
  margin-bottom: 1rem;
`;

const Username = styled.div`
  font-size: 1.5rem;
  font-weight: 600;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin: 0.5rem 0;
`;

const TagButton = styled.button`
  padding: 0.5rem 1.2rem;
  border-radius: 20px;
  border: none;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.2s ease;

  &:first-child {
    background: linear-gradient(135deg, #6e8efb, #a777e3);
    color: white;
  }

  &:last-child {
    background: rgba(255, 255, 255, 0.1);
    color: white;
    border: 1px solid rgba(255, 255, 255, 0.3);
  }
`;

const StatsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin: 1rem 0;
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.3rem;
`;

const StatLabel = styled.div`
  font-size: 0.7rem;
  opacity: 0.6;
  font-weight: 500;
`;

const StatValue = styled.div`
  font-size: 0.8rem;
  font-weight: 700;
`;

// New Countdown Timer Styles
const CountdownTimer = styled.div`
  width: 100%;
  text-align: center;
  background: #111827;
  padding: 1rem;
  border-radius: 8px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
`;

const TimerLabel = styled.div`
  font-size: 0.7rem;
  margin-bottom: 0.5rem;
  opacity: 0.9;
  color: #ccff00;
`;

const TimerDisplay = styled.div`
  font-size: 2rem;
  font-weight: 700;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  display: inline-block;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  color: #ccff00;
`;

const ProgressContainer = styled.div`
  width: 100%;
  margin-top: 0.5rem;
`;

const ProgressBar = styled.div`
  height: 8px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 0.5rem;
`;

const ProgressFill = styled.div<{ progress: number }>`
  height: 100%;
  width: ${(props) => props.progress}%;
  background: linear-gradient(90deg, #55631eff, #ccff00);
  border-radius: 4px;
  transition: width 0.3s ease;
`;

const ProgressText = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.7rem;
  margin-bottom: 0.4rem;

  span {
    opacity: 0.6;
  }
`;

const IconsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
  padding: 1rem;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Icon = styled.img`
  width: 26px;
  height: 26px;
  filter: brightness(0) invert(1);
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }
`;

const AvatarImage = styled.img`
  width: 74px;
  height: 74px;
  border-radius: 25%;
`;

const UsernameImage = styled.div`
  font-size: 1.2rem;
  text-transform: uppercase;
`;

const Title = styled.div`
  font-size: 1.5rem;
  font-weight: 800;
  color: white;
  margin-bottom: 0.5rem;
  background: #fff;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-align: center;
`;

interface UserProfileProps {
  user: any;
  showAvatar?: boolean;
  profile?: boolean;
  onPause?: () => void;
  isPaused?: boolean;
  settings?: boolean;
  onMute?: () => void;
  isMuted?: boolean;
  remainingTime?: number;
  visible?: boolean;
  scoreChange?: "positive" | "negative" | null;
  mini?: boolean;
}

const UserProfile = ({
  user,
  showAvatar = true,
  profile = false,
  onPause,
  isPaused,
  onMute,
  isMuted,
  settings = false,
  scoreChange,
  visible = true,
  remainingTime,
  mini = false,
}: UserProfileProps) => {
  const navigate = useNavigate();
  const [backgroundColor, setBackgroundColor] = useState("#6e8efb");
  const [timeLeft, setTimeLeft] = useState(remainingTime || 0);

  useEffect(() => {
    // Generate a   pastel color for the background based on the user's username
    const pastelColor = generatePastelColor(user.username);

    setBackgroundColor(pastelColor);
  }, []);

  const generatePastelColor = (username: string) => {
    const hash = username?.split("").reduce((acc, char) => {
      return acc + char.charCodeAt(0);
    }, 0);
    const hue = hash % 360;
    return `hsl(${hue}, 50%, 75%)`;
  };
  useEffect(() => {
    setTimeLeft(remainingTime || 0);
  }, [remainingTime]);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [remainingTime]);

  const formatTime = (seconds: number) => {
    // in this format 00:00:00
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const handleSettingsClick = () => {
    navigate("/profile");
  };

  const handleMuteClick = () => {
    if (onMute) {
      onMute();
    }
  };

  const handlePauseClick = () => {
    if (onPause) {
      onPause();
    }
  };

  // Mock data for the new UI elements
  const stats = {
    streaks: user?.lifetimeLoginDays + user?.dailyStreak || 0,
    rank: user?.rank || "NA",
    xp: user?.points || 0,
    level: getLevel(user?.points || 0).level,
    levelProgress: getLevel(user?.points || 0).percentage,
  };

  return (
    <ProfileContainer
      style={{ backgroundColor: !mini ? backgroundColor : "transparent" }}
    >
      {!mini && (
        <>
          <BackgroundOverlay />
          <IconsContainer>
            <IconButton
              onClick={handlePauseClick}
              style={{ display: showAvatar ? "none" : "flex" }}
            >
              <Icon
                src={isPaused ? PlayIcon : PauseIcon}
                alt={isPaused ? "Play" : "Pause"}
              />
            </IconButton>

            <IconButton
              onClick={handleMuteClick}
              style={{ display: showAvatar ? "none" : "flex" }}
            >
              <Icon
                src={!isMuted ? UnmuteIcon : MuteIcon}
                alt={isMuted ? "Unmute" : "Mute"}
              />
            </IconButton>
          </IconsContainer>

          <AvatarWrapper
            onClick={() => {
              if (!profile) return;
              navigate("/profile");
            }}
          >
            {user?.profilePicture ? (
              <AvatarImage src={user?.profilePicture} alt="User Avatar" />
            ) : (
              <UsernameImage>
                {user ? user.username.slice(0, 2) : "HI"}
              </UsernameImage>
            )}
          </AvatarWrapper>

          <Username>{user ? user.username : "User"}</Username>

          <ButtonContainer>
            <TagButton>Contact</TagButton>
            <TagButton>Player</TagButton>
          </ButtonContainer>
        </>
      )}

      <StatsContainer>
        <StatItem>
          <StatLabel>Streaks</StatLabel>
          <StatValue>{stats.streaks} Days</StatValue>
        </StatItem>
        <StatItem>
          <StatLabel>Leaderboard Rank</StatLabel>
          <StatValue>⭐ #{stats.rank}</StatValue>
        </StatItem>
        <StatItem>
          <StatLabel>XP Points</StatLabel>
          <StatValue>⭐ {stats.xp}</StatValue>
        </StatItem>
      </StatsContainer>

      {mini && (
        <CountdownTimer>
          <TimerDisplay>{formatTime(timeLeft)}</TimerDisplay>
          <TimerLabel>Time Left</TimerLabel>
        </CountdownTimer>
      )}

      <ProgressContainer>
        <ProgressText>
          <span>Progress</span>
          <span>Level {stats.level}</span>
        </ProgressText>
        <ProgressBar>
          <ProgressFill progress={stats.levelProgress} />
        </ProgressBar>
      </ProgressContainer>
    </ProfileContainer>
  );
};

export default UserProfile;
